/**
 * Клиент оплаты: создаёт платёж на сервере (/api/pay), опрашивает статус и забирает набор (/api/claim).
 * Незавершённый платёж хранится в localStorage, чтобы после возврата из банковского приложения набор не потерялся.
 */
const PID_KEY = 'paradise-isle:pid';
const PENDING_KEY = 'paradise-isle:pendingPay';

export interface PendingPay { id: string; packId: string; rub: number; demo: boolean }
export type PayStatus = 'none' | 'pending' | 'canceled' | { packId: string };

export function playerId(): string {
  try {
    let id = localStorage.getItem(PID_KEY);
    if (!id) {
      id = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now();
      localStorage.setItem(PID_KEY, id);
    }
    return id;
  } catch { return 'anon'; }
}

export function getPending(): PendingPay | null {
  try { return JSON.parse(localStorage.getItem(PENDING_KEY) ?? 'null') as PendingPay | null; } catch { return null; }
}
function setPending(p: PendingPay | null): void {
  try { if (p) localStorage.setItem(PENDING_KEY, JSON.stringify(p)); else localStorage.removeItem(PENDING_KEY); } catch { /* ignore */ }
}

async function api<T>(path: string, body?: unknown): Promise<T> {
  const r = await fetch(path, body ? { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) } : undefined);
  const j = (await r.json().catch(() => ({}))) as { error?: string } & T;
  if (!r.ok) throw new Error(j.error || String(r.status));
  return j;
}

export interface PayConfig { enabled: boolean; live: boolean; demo: boolean }
export async function payConfig(): Promise<PayConfig> {
  try { return await api<PayConfig>('/api/config'); } catch { return { enabled: false, live: false, demo: false }; }
}

/** Создать платёж. Для боевого режима открывает страницу СБП в новой вкладке. */
export async function startPayment(packId: string, rub: number): Promise<PendingPay> {
  const r = await api<{ id: string; url?: string; demo?: boolean }>('/api/pay', { pack: packId, player: playerId() });
  const pending: PendingPay = { id: r.id, packId, rub, demo: !!r.demo };
  setPending(pending);
  if (r.url) window.open(r.url, '_blank', 'noopener');
  return pending;
}

/** Проверить незавершённый платёж. Если оплачен — забрать набор (сервер отдаёт его один раз). */
export async function checkPending(): Promise<PayStatus> {
  const p = getPending();
  if (!p) return 'none';
  const st = await api<{ status: string }>(`/api/status?id=${encodeURIComponent(p.id)}`);
  if (st.status === 'succeeded') {
    const c = await api<{ pack: string }>('/api/claim', { id: p.id, player: playerId() });
    setPending(null);
    return { packId: c.pack || p.packId };
  }
  if (st.status === 'canceled') { setPending(null); return 'canceled'; }
  return 'pending';
}

export async function demoPay(): Promise<void> {
  const p = getPending();
  if (p) await api('/api/demo-pay', { id: p.id });
}
export function cancelPending(): void { setPending(null); }
